import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { generatePathDatacentral, paths } from '@/utils/path'
import { ArrowUpRight, User, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { getRelatedBusinesses } from '../../../services'
import { ParentBusiness, RelatedBusiness } from '../../../types/response'
import { BUSINESS_STATUS, formatDate, getTextColor } from '../../../types/utils'

interface RelatedBusinessesProps {
    businessId: number
    companionCount?: number
    parentBusiness?: ParentBusiness
}

const RelatedBusinesses: React.FC<RelatedBusinessesProps> = ({
    businessId,
    companionCount,
    parentBusiness,
}) => {
    const { t } = useTranslation()

    const { data: relatedData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.BUSINESS_RELATED, businessId],
        queryFn: async () => {
            const result = await getRelatedBusinesses(businessId)
            return result
        },
        enabled: !!businessId,
    })

    const stateComponent = renderState()

    const relateds = relatedData?.results || []
    const hasRelateds = relateds.length > 0 || parentBusiness

    if (!hasRelateds && !stateComponent) {
        return null
    }

    const getStatusLabel = (status: string) => {
        return (
            BUSINESS_STATUS(t).find((s) => s.value === status)?.label ||
            t('unknown')
        )
    }

    const renderBusinessCard = (
        business: RelatedBusiness | ParentBusiness,
        role: 'parent' | 'companion'
    ) => {
        const isParent = role === 'parent'
        return (
            <div
                key={business.business_id}
                className="border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        {isParent ? (
                            <User className="w-4 h-4 text-blue-500" />
                        ) : (
                            <Users className="w-4 h-4 text-green-500" />
                        )}
                        <div>
                            <Link
                                to={generatePathDatacentral(
                                    paths.businessAccountDetail,
                                    { id: business.business_id }
                                )}
                                className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                            >
                                IBN-{business.business_id}
                                <ArrowUpRight className="w-3 h-3" />
                            </Link>
                            <p className="text-sm text-gray-600">
                                {business.employee_name}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <Chip
                            variant="contained"
                            label={
                                isParent
                                    ? t('business-role-parent')
                                    : t('business-role-companion')
                            }
                            color={isParent ? 'primary' : 'secondary'}
                            className="text-xs"
                        />
                        <span
                            className={`text-xs font-medium ${getTextColor(business.business_status)}`}
                        >
                            {getStatusLabel(business.business_status)}
                        </span>
                    </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div>
                        <span className="text-gray-500">
                            {t('department')}:
                        </span>{' '}
                        <span className="font-medium">
                            {business.department_name}
                        </span>
                    </div>
                    <div>
                        <span className="text-gray-500">{t('position')}:</span>{' '}
                        <span className="font-medium">
                            {business.position_name}
                        </span>
                    </div>
                    <div className="col-span-2">
                        <span className="text-gray-500">
                            {t('business-startdate')}:
                        </span>{' '}
                        <span className="font-medium">
                            {formatDate(business.business_startdate)} -{' '}
                            {formatDate(business.business_enddate)}
                        </span>
                    </div>
                    {business.business_place && (
                        <div className="col-span-2">
                            <span className="text-gray-500">
                                {t('business-place')}:
                            </span>{' '}
                            <span className="font-medium">
                                {business.business_place}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="bg-white mt-2 rounded-[8px] p-4">
            <div className="flex items-center justify-between mb-4">
                <Typogaphy className="font-medium" size="small">
                    {t('related-businesses')}
                </Typogaphy>
                {companionCount !== undefined && companionCount > 0 && (
                    <Chip
                        variant="contained"
                        color="secondary"
                        className="text-xs"
                        label={`${companionCount} ${t('companions')}`}
                    />
                )}
            </div>

            {stateComponent ? (
                stateComponent
            ) : (
                <div className="space-y-3">
                    {parentBusiness &&
                        renderBusinessCard(parentBusiness, 'parent')}

                    {relateds?.map((related: RelatedBusiness) =>
                        renderBusinessCard(related, related.business_role)
                    )}
                </div>
            )}
        </div>
    )
}

export default RelatedBusinesses
